import { FC, useCallback, useContext, useRef, useState } from 'react';
import { Path } from '../../Path';
import { DescriptionWrapper } from './PathDescription.styled';
import { ZoneContext } from 'context/ZoneContext';
import { useOutsideAlerter } from 'utils/utils';
import { getLabelPosition } from './utils';
import { Text } from '@medly-components/core';
import { zIndex } from 'theme/zIndex';
import useMapSearch from 'hooks/search/useMapSearch';
import { MapSearchContext } from 'context/MapSearchContext';

type PathDescriptionExpanded = PathDescription & {
    setIsExpanded: (isExpanded: boolean) => void;
};
export const PathDescriptionExpanded: FC<PathDescriptionExpanded> = ({ path, setIsExpanded }) => {
    const { paths, expandedPathDescription, setExpandedPathDescription, scale } = useContext(ZoneContext);
    const { description } = path;
    const { setActiveSearchId } = useContext(MapSearchContext);
    const { getSearchColor, getSearchStatus, hasOwnSearchResult } = useMapSearch();

    const handleOnClick = () => {
        hasOwnSearchResult(path.getId()) && setActiveSearchId(path.getId());
    };

    const onCollapse = useCallback(() => {
        setIsExpanded(false);
        setExpandedPathDescription(undefined);
    }, []);

    const descriptionWrapperRef = useRef<HTMLDivElement>(null);
    useOutsideAlerter(descriptionWrapperRef, onCollapse, { capture: true });

    const position = getLabelPosition(path, paths);
    if (!position) {
        return null;
    }

    const searchStatus = getSearchStatus(path.getId());
    const searchColor = getSearchColor(path.getId());
    const style = {
        left: position.x,
        top: position.y,
        zIndex: path.getId() === expandedPathDescription?.getId() ? zIndex.PathDescription : 1,
        backgroundColor: searchColor || ''
    };
    return (
        <DescriptionWrapper
            id={path.getId()}
            ref={descriptionWrapperRef}
            style={style}
            isExpanded={true}
            onMouseLeave={onCollapse}
            type={position.type}
            scale={scale}
            className={searchStatus || ''}
            onClick={handleOnClick}
        >
            <Text>{description}</Text>
        </DescriptionWrapper>
    );
};

type PathDescription = {
    path: Path;
};
export const PathDescription: FC<PathDescription> = ({ path }) => {
    const { expandedStation, paths, expandedPathDescription, setExpandedPathDescription, scale } = useContext(ZoneContext);
    const { description } = path;
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { setActiveSearchId } = useContext(MapSearchContext);
    const { getSearchColor, getSearchStatus, hasOwnSearchResult } = useMapSearch();

    const handleOnClick = () => {
        hasOwnSearchResult(path.getId()) && setActiveSearchId(path.getId());
    };

    const onExpand = useCallback(() => {
        setIsExpanded(true);
        setExpandedPathDescription(path);
    }, []);

    if (!description || path.isHidden(expandedStation)) {
        return null;
    }

    if (isExpanded) {
        return <PathDescriptionExpanded path={path} setIsExpanded={setIsExpanded} />;
    }

    const position = getLabelPosition(path, paths);
    if (!position) {
        return null;
    }

    const searchStatus = getSearchStatus(path.getId());
    const searchColor = getSearchColor(path.getId());
    const showExpandedText = description.length <= 4;
    const style = {
        left: position.x,
        top: position.y,
        zIndex: path.getId() === expandedPathDescription?.getId() ? zIndex.PathDescription : 1,
        backgroundColor: searchColor || ''
    };

    return (
        <DescriptionWrapper
            id={path.getId()}
            style={style}
            isExpanded={showExpandedText}
            onMouseEnter={onExpand}
            type={position.type}
            scale={scale}
            className={searchStatus || ''}
            onClick={handleOnClick}
        >
            {showExpandedText ? <Text>{description}</Text> : <span>...</span>}
        </DescriptionWrapper>
    );
};
